import { useState } from 'react'
import { createPortal } from 'react-dom'
import { ImageIcon, X } from 'lucide-react'
import type { ChartData } from '@/service/api'
import { useTranslation } from '@/i18n'

interface ModalChart {
  src: string
  name: string
}

interface TimeseriesChartsProps {
  charts?: string[]
  chart_data?: ChartData[]
}

const TimeseriesCharts = ({ charts, chart_data }: TimeseriesChartsProps) => {
  const [modalChart, setModalChart] = useState<ModalChart | null>(null)
  const { t } = useTranslation()

  // Only render if we have charts to show
  const hasCharts = (charts && charts.length > 0) || (chart_data && chart_data.length > 0)
  if (!hasCharts) return null

  return (
    <>
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-3">
          <ImageIcon size={16} className="text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('chat:messages.charts.generated', {
              defaultValue: 'Generated Charts ({{count}}):',
              count: chart_data?.length || charts?.length || 0,
            })}
          </span>
        </div>
        <div className="grid gap-4">
          {/* The chart function returns a standalone Plotly HTML document, rendered
              in a sandboxed iframe. Prefer chart_data (base64 HTML) if available,
              fall back to the chart-URL strings. */}
          {(chart_data || charts)?.map((chartItem, index) => {
            const isChartData = typeof chartItem === 'object'
            const chartSrc = isChartData
              ? ((chartItem as ChartData).data || (chartItem as ChartData).url || '')
              : chartItem
            const chartName = isChartData
              ? (chartItem as ChartData).filename
              : t('chat:messages.charts.chart_name', { defaultValue: 'Chart {{number}}', number: index + 1 })

            return (
              /* No outline: the chart is drawn on the chat's own surface, so a border
                 around it reads as a box sitting on top of the message. */
              <div key={index} className="rounded-lg overflow-hidden bg-white dark:bg-dark-card">
                <div
                  className="relative cursor-pointer"
                  onClick={() => setModalChart({ src: chartSrc, name: chartName })}
                >
                  <iframe
                    src={chartSrc}
                    title={t('chat:messages.charts.chart_title', { defaultValue: 'Chart: {{name}}', name: chartName })}
                    className="w-full h-96 block border-0 bg-white dark:bg-dark-card pointer-events-none"
                    sandbox="allow-scripts"
                  />
                </div>

                {/* Chart info. The chart names itself in its own heading, so the footer
                    carries only its weight — a filename says nothing to the reader. */}
                {isChartData && !!(chartItem as ChartData).size && (
                  <div className="p-2">
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {Math.round(((chartItem as ChartData).size || 0) / 1024)}KB
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Chart Modal — portalled to the body.

          `fixed inset-0` is only the viewport when no ancestor establishes a containing
          block, and a `transform`, `filter`, `backdrop-filter` or `contain` anywhere above
          this message does establish one. The backdrop then covers that ancestor instead
          and leaves the page showing along an edge. Rendering at the body removes the
          question, and keeps it removed as the chat's layout changes around it. */}
      {modalChart && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setModalChart(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center justify-center">
            <button
              className="absolute top-4 right-4 z-10 bg-white dark:bg-dark-card rounded-full p-2 hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors shadow-lg"
              onClick={(e) => {
                e.stopPropagation()
                setModalChart(null)
              }}
            >
              <X size={20} className="text-gray-700 dark:text-gray-300" />
            </button>
            <iframe
              src={modalChart.src}
              title={t('chat:messages.charts.full_size_title', { defaultValue: 'Full size: {{name}}', name: modalChart.name })}
              className="w-[85vw] h-[80vh] bg-white dark:bg-dark-card rounded-lg shadow-2xl border-0"
              sandbox="allow-scripts"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

export default TimeseriesCharts
